import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Building2,
  Users,
  FolderKanban,
  BarChart3,
  Calendar,
  FileText,
  Shield,
  Zap,
  CheckCircle2,
  ArrowRight,
  Menu,
  X,
  Star,
  Clock,
  DollarSign,
  Smartphone,
  HardHat,
  ClipboardList,
  TrendingUp,
  AlertCircle,
  Send,
  MessageSquarePlus,
  Rocket,
} from 'lucide-react';
import { toast } from 'sonner';
import CookieConsent from '../components/CookieConsent';
import { testimonialsApi, betaApi } from '../lib/api';
import { useTheme } from '../context/ThemeContext';

const FEATURES = [
  {
    icon: Users,
    title: 'Lead Management',
    description: 'Track every lead from first contact to signed contract. Never miss a follow-up with automated reminders.',
  },
  {
    icon: FolderKanban,
    title: 'Project Tracking',
    description: 'Monitor every phase from permits to final inspection. Keep clients informed with real-time progress updates.',
  },
  {
    icon: DollarSign,
    title: 'Estimates & Invoicing',
    description: 'Turn estimates into invoices in a click — bill deposits, progress draws, or final amounts. Combine multiple estimates, keep your line-item pricing private from clients, and see exactly how much of each contract you\'ve invoiced. Export to CSV for your accountant.',
  },
  {
    icon: ClipboardList,
    title: 'Cost Estimating',
    description: 'Import estimates from Craftsman National Estimator. Track budget vs actual per job with real-time cost visibility.',
  },
  {
    icon: HardHat,
    title: 'Team & Subcontractors',
    description: 'Manage your crew with role-based access. Maintain a directory of trusted subcontractors.',
  },
  {
    icon: BarChart3,
    title: 'Reports & Analytics',
    description: 'Pipeline funnels, revenue trends, lead sources, and project health — all in one dashboard with date filters.',
  },
  {
    icon: Calendar,
    title: 'Scheduling',
    description: 'Sync with Google Calendar. Schedule appointments, set reminders, and never double-book again.',
  },
  {
    icon: FileText,
    title: 'Document & Photo Storage',
    description: 'Upload site photos, contracts, and permits directly from your phone. iPhone HEIC and ProRAW supported — auto-converted for web viewing.',
  },
  {
    icon: Shield,
    title: 'Client Portal',
    description: 'Give clients a branded portal to view project progress, photos, and timelines. Share with a single link.',
  },
];

// Honest value propositions based on real pain points
const VALUE_PROPS = [
  { 
    icon: Clock, 
    value: '10+ hrs/week', 
    label: 'Saved on Admin',
    description: 'Stop juggling spreadsheets, texts, and sticky notes'
  },
  { 
    icon: AlertCircle, 
    value: 'Zero', 
    label: 'Missed Follow-ups',
    description: 'Automated reminders keep leads from falling through cracks'
  },
  { 
    icon: Users, 
    value: '1 Place', 
    label: 'For Everything',
    description: 'Leads, projects, clients, and team—all in one system'
  },
  { 
    icon: Smartphone, 
    value: '24/7', 
    label: 'Client Access',
    description: 'Clients check progress anytime without calling you'
  },
];

const PAIN_POINTS = [
  { id: 'lost_leads', label: 'Lost leads' },
  { id: 'sub_coordination', label: 'Sub coordination' },
  { id: 'follow_ups', label: 'Follow-ups' },
  { id: 'estimating_chaos', label: 'Estimating chaos' },
  { id: 'other', label: 'Other' },
];

const EMPLOYEE_OPTIONS = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5 employees' },
  { value: '6-10', label: '6-10 employees' },
  { value: '11-25', label: '11-25 employees' },
  { value: '26+', label: '26+ employees' },
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [publishedTestimonials, setPublishedTestimonials] = useState([]);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    company: '',
    role: '',
    email: '',
    quote: '',
    rating: 5
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Beta popup state
  const [showBetaPopup, setShowBetaPopup] = useState(false);
  const [betaForm, setBetaForm] = useState({
    email: '',
    first_name: '',
    company_name: '',
    phone: '',
    num_employees: '',
    pain_points: [],
    other_pain_point: '',
    zip_code: '',
  });
  const [betaSubmitting, setBetaSubmitting] = useState(false);
  const [betaSubmitted, setBetaSubmitted] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Force light mode on the landing page regardless of user preference
  const { setForceLightMode } = useTheme();
  
  useEffect(() => {
    setForceLightMode(true);
    return () => setForceLightMode(false);
  }, [setForceLightMode]);

  useEffect(() => {
    // Load published testimonials
    const loadTestimonials = async () => {
      try {
        const response = await testimonialsApi.getPublished();
        setPublishedTestimonials(response.data.testimonials || []);
      } catch (error) {
        console.error('Failed to load testimonials:', error);
      }
    };
    loadTestimonials();
  }, []);

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    if (!testimonialForm.name || !testimonialForm.email || !testimonialForm.quote) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await testimonialsApi.submit(testimonialForm);
      toast.success('Thank you! Your testimonial will be reviewed before publishing.');
      setShowTestimonialForm(false);
      setTestimonialForm({ name: '', company: '', role: '', email: '', quote: '', rating: 5 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBetaPainPointToggle = (pointId) => {
    setBetaForm(prev => ({
      ...prev,
      pain_points: prev.pain_points.includes(pointId)
        ? prev.pain_points.filter(p => p !== pointId)
        : [...prev.pain_points, pointId]
    }));
  };

  const handleBetaSubmit = async (e) => {
    e.preventDefault();
    if (!betaForm.email || !betaForm.first_name || !betaForm.company_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setBetaSubmitting(true);
    try {
      const response = await betaApi.signup(betaForm);
      setBetaSubmitted(true);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setBetaSubmitting(false);
    }
  };

  const closeBetaPopup = () => {
    setShowBetaPopup(false);
    setBetaSubmitted(false);
    setBetaForm({
      email: '',
      first_name: '',
      company_name: '',
      phone: '',
      num_employees: '',
      pain_points: [],
      other_pain_point: '',
      zip_code: '',
    });
  };

  return (
    <div className="force-light-mode min-h-screen bg-white" data-testid="landing-page">
      <CookieConsent />
      
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-md bg-white' : 'bg-white/95'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-sm flex items-center justify-center bg-orange-500">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">Corbel</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Features</a>
              <a href="#testimonials" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Testimonials</a>
              <Link to="/pricing" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Pricing</Link>
              <Link to="/contact" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Contact</Link>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4">
              <Button 
                variant="outline" 
                className="rounded-sm border-orange-500 text-orange-500 hover:bg-orange-50"
                onClick={() => setShowBetaPopup(true)}
              >
                <Rocket className="h-4 w-4 mr-2" />
                Join Beta
              </Button>
              <Link to="/login">
                <Button variant="ghost" className="rounded-sm text-slate-700 hover:text-slate-900 hover:bg-slate-100">Sign In</Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-slate-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t shadow-lg bg-white">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block py-2 font-medium text-slate-700" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#testimonials" className="block py-2 font-medium text-slate-700" onClick={() => setMobileMenuOpen(false)}>Testimonials</a>
              <Link to="/pricing" className="block py-2 font-medium text-slate-700" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <Link to="/contact" className="block py-2 font-medium text-slate-700" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <div className="pt-4 space-y-2">
                <Button 
                  className="w-full rounded-sm bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => { setMobileMenuOpen(false); setShowBetaPopup(true); }}
                >
                  <Rocket className="h-4 w-4 mr-2" />
                  Join Beta Waitlist
                </Button>
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full rounded-sm text-slate-700 border-slate-300">Sign In</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 bg-orange-100 text-orange-700"
            >
              <Zap className="h-4 w-4" />
              Built specifically for residential contractors
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-slate-900">
              The CRM That Helps You <span className="text-orange-500">Build More</span> & Stress Less
            </h1>
            <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-slate-600">
              Manage leads, track projects, and keep clients happy—all in one place. 
              Built by people who understand the construction business.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="rounded-sm bg-orange-500 hover:bg-orange-600 text-lg px-8 h-14">
                  Start Your Free 30-Day Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="rounded-sm text-lg px-8 h-14 text-slate-700 border-slate-300 hover:bg-slate-100">
                  View Pricing
                </Button>
              </Link>
            </div>
            <p className="text-sm text-slate-500 mt-4">
              No credit card required • Cancel anytime
            </p>
          </div>

          {/* Hero Image/Dashboard Preview */}
          <div className="mt-16 relative">
            <div className="bg-slate-900 rounded-xl shadow-2xl p-2 md:p-4 max-w-5xl mx-auto">
              <div className="bg-slate-800 rounded-lg p-4 md:p-6">
                {/* KPI Cards - matching actual dashboard */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-white rounded-sm p-4 border-t-4 border-t-orange-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 font-medium">Total Leads</p>
                        <p className="text-2xl font-bold text-slate-900">24</p>
                      </div>
                      <div className="h-10 w-10 bg-orange-100 rounded-sm flex items-center justify-center">
                        <Users className="h-5 w-5 text-orange-500" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-sm p-4 border-t-4 border-t-blue-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 font-medium">Pipeline Value</p>
                        <p className="text-2xl font-bold text-slate-900">$482K</p>
                      </div>
                      <div className="h-10 w-10 bg-blue-100 rounded-sm flex items-center justify-center">
                        <DollarSign className="h-5 w-5 text-blue-500" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-sm p-4 border-t-4 border-t-green-600">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 font-medium">Won Deals</p>
                        <p className="text-2xl font-bold text-slate-900">$128K</p>
                      </div>
                      <div className="h-10 w-10 bg-green-100 rounded-sm flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-green-600" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-sm p-4 border-t-4 border-t-slate-900">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 font-medium">Active Projects</p>
                        <p className="text-2xl font-bold text-slate-900">12</p>
                      </div>
                      <div className="h-10 w-10 bg-slate-100 rounded-sm flex items-center justify-center">
                        <FolderKanban className="h-5 w-5 text-slate-900" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Simplified dashboard content */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Recent Leads */}
                  <div className="bg-white rounded-sm p-4">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Recent Leads</h3>
                    <div className="space-y-2">
                      {[
                        { name: 'Johnson Kitchen Remodel', value: '$45,000', stage: 'Qualified' },
                        { name: 'Smith Bathroom Addition', value: '$32,000', stage: 'Proposal' },
                        { name: 'Davis Deck Build', value: '$18,500', stage: 'New' },
                      ].map((lead, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{lead.name}</p>
                            <p className="text-xs text-slate-500">{lead.value}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            lead.stage === 'Qualified' ? 'bg-green-100 text-green-700' :
                            lead.stage === 'Proposal' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {lead.stage}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Active Projects */}
                  <div className="bg-white rounded-sm p-4">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Active Projects</h3>
                    <div className="space-y-2">
                      {[
                        { name: 'Williams Home Addition', phase: 'Framing', progress: 45 },
                        { name: 'Brown Master Suite', phase: 'Electrical', progress: 60 },
                        { name: 'Miller Garage Build', phase: 'Foundation', progress: 25 },
                      ].map((project, i) => (
                        <div key={i} className="py-2 border-b border-slate-100 last:border-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-medium text-slate-900">{project.name}</p>
                            <span className="text-xs text-slate-500">{project.phase}</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-orange-500 h-2 rounded-full" 
                              style={{ width: `${project.progress}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section - Honest stats about pain points solved */}
      <section className="py-12 bg-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {VALUE_PROPS.map((prop, i) => (
              <div key={i} className="text-center">
                <prop.icon className="h-8 w-8 text-orange-200 mx-auto mb-2" />
                <p className="text-3xl md:text-4xl font-bold text-white">{prop.value}</p>
                <p className="text-orange-100 font-medium">{prop.label}</p>
                <p className="text-orange-200 text-sm mt-1 hidden md:block">{prop.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need to Run Your Business
            </h2>
            <p className="text-lg text-slate-600">
              From first contact to final walkthrough, Corbel keeps everything organized and your team on the same page.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {FEATURES.map((feature, i) => (
              <Card key={i} className="rounded-sm border-2 hover:border-orange-200 transition-colors">
                <CardContent className="p-6">
                  <div className="h-12 w-12 bg-orange-100 rounded-sm flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-lg text-slate-600">
              No complex setup. No training required. Just sign up and start managing your business better.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Sign Up', description: 'Create your account in under 2 minutes. No credit card required for your 30-day trial.' },
              { step: '2', title: 'Add Your Data', description: 'Import your leads and projects, or start fresh. We\'ll guide you through the process.' },
              { step: '3', title: 'Grow Your Business', description: 'Start closing more deals, delighting clients, and spending less time on paperwork.' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="h-16 w-16 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What Contractors Are Saying
            </h2>
            <p className="text-lg text-slate-600">
              {publishedTestimonials.length > 0 
                ? 'Real feedback from real contractors using Corbel.'
                : 'We\'re just getting started! Be one of the first to share your experience.'}
            </p>
          </div>

          {/* Published Testimonials */}
          {publishedTestimonials.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {publishedTestimonials.map((testimonial, i) => (
                <Card key={i} className="rounded-sm">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-6 italic">&quot;{testimonial.quote}&quot;</p>
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">
                        {testimonial.role && `${testimonial.role}, `}{testimonial.company}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Testimonial Submission CTA */}
          <div className="text-center">
            {!showTestimonialForm ? (
              <div className="bg-slate-50 rounded-lg p-8 max-w-2xl mx-auto">
                <MessageSquarePlus className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  Using Corbel? Share Your Experience!
                </h3>
                <p className="text-slate-600 mb-6">
                  We&apos;d love to hear how Corbel is helping your business. Your feedback helps us improve and helps other contractors make informed decisions.
                </p>
                <Button 
                  onClick={() => setShowTestimonialForm(true)}
                  className="rounded-sm bg-orange-500 hover:bg-orange-600"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit a Testimonial
                </Button>
              </div>
            ) : (
              <Card className="max-w-2xl mx-auto rounded-sm">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900 mb-6">Share Your Experience</h3>
                  <form onSubmit={handleTestimonialSubmit} className="space-y-4 text-left">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700">Your Name *</Label>
                        <Input
                          value={testimonialForm.name}
                          onChange={(e) => setTestimonialForm({...testimonialForm, name: e.target.value})}
                          placeholder="John Smith"
                          className="rounded-sm mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">Company Name</Label>
                        <Input
                          value={testimonialForm.company}
                          onChange={(e) => setTestimonialForm({...testimonialForm, company: e.target.value})}
                          placeholder="Smith Construction"
                          className="rounded-sm mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700">Your Role</Label>
                        <Input
                          value={testimonialForm.role}
                          onChange={(e) => setTestimonialForm({...testimonialForm, role: e.target.value})}
                          placeholder="Owner"
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">Email *</Label>
                        <Input
                          type="email"
                          value={testimonialForm.email}
                          onChange={(e) => setTestimonialForm({...testimonialForm, email: e.target.value})}
                          placeholder="john@example.com"
                          className="rounded-sm mt-1"
                          required
                        />
                        <p className="text-xs text-slate-500 mt-1">We won&apos;t publish your email</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700">Rating</Label>
                      <div className="flex gap-1 mt-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setTestimonialForm({...testimonialForm, rating: star})}
                            className="focus:outline-none"
                          >
                            <Star 
                              className={`h-8 w-8 ${star <= testimonialForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} 
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700">Your Testimonial *</Label>
                      <Textarea
                        value={testimonialForm.quote}
                        onChange={(e) => setTestimonialForm({...testimonialForm, quote: e.target.value})}
                        placeholder="Tell us how Corbel has helped your business..."
                        className="rounded-sm mt-1"
                        rows={4}
                        required
                      />
                    </div>
                    <div className="flex gap-3 justify-end pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowTestimonialForm(false)}
                        className="rounded-sm"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="rounded-sm bg-orange-500 hover:bg-orange-600"
                      >
                        {submitting ? 'Submitting...' : 'Submit Testimonial'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 md:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-300">
              Start free, upgrade when you&apos;re ready. No hidden fees.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { name: 'Base', price: '$19', desc: 'Perfect for solo contractors', features: ['Unlimited leads & projects', 'Client portal', 'Document storage', 'Email reminders'] },
              { name: 'Pro', price: '$29', desc: 'For growing teams', features: ['Everything in Base', '2 team members included', 'Priority support', 'Advanced reporting'], popular: true },
              { name: 'Business', price: '$79', desc: 'For established companies', features: ['Everything in Pro', 'Unlimited team members', 'White-label portal', 'API access'] },
            ].map((plan, i) => (
              <div key={i} className={`bg-slate-800 rounded-sm p-6 ${plan.popular ? 'ring-2 ring-orange-500' : ''}`}>
                {plan.popular && (
                  <span className="inline-block bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">Most Popular</span>
                )}
                <h3 className="text-xl font-semibold text-white mb-1">{plan.name}</h3>
                <p className="text-slate-400 text-sm mb-4">{plan.desc}</p>
                <p className="text-4xl font-bold text-white mb-1">{plan.price}<span className="text-lg text-slate-400">/mo</span></p>
                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="h-5 w-5 text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/pricing" className="block mt-6">
                  <Button className={`w-full rounded-sm ${plan.popular ? 'bg-orange-500 hover:bg-orange-600' : 'bg-slate-700 hover:bg-slate-600'}`}>
                    Get Started
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/pricing" className="text-orange-400 hover:text-orange-300 font-medium">
              View full pricing details →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Ready to Build a Better Business?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join contractors who are simplifying their workflow with Corbel.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="rounded-sm bg-orange-500 hover:bg-orange-600 text-lg px-8 h-14">
                Start Your Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="rounded-sm text-lg px-8 h-14 text-slate-700 border-slate-300 hover:bg-slate-100">
                Talk to Sales
              </Button>
            </Link>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500">
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> 30-day free trial</span>
            <span className="flex items-center gap-1"><Shield className="h-4 w-4" /> No credit card required</span>
            <span className="flex items-center gap-1"><Zap className="h-4 w-4" /> Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 bg-orange-500 rounded-sm flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <span className="font-bold text-white">Corbel</span>
              </div>
              <p className="text-slate-400 text-sm">
                The CRM built for residential construction companies.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/login" className="hover:text-white">Sign In</Link></li>
                <li><Link to="/register" className="hover:text-white">Start Trial</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Support</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="mailto:support@corbel.work" className="hover:text-white">support@corbel.work</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-400 text-sm">
            © {new Date().getFullYear()} Corbel. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Beta Signup Popup */}
      <Dialog open={showBetaPopup} onOpenChange={closeBetaPopup}>
        <DialogContent className="force-light-mode max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Rocket className="h-5 w-5 text-orange-500" />
              Join the Corbel Waitlist
            </DialogTitle>
            <p className="text-slate-500 text-sm mt-1">
              Get Early Access + Help Shape the Tool
            </p>
          </DialogHeader>
          
          {betaSubmitted ? (
            <div className="text-center py-6">
              <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">You&apos;re on the list!</h3>
              <p className="text-slate-600 mb-4">
                We&apos;ll reach out soon with early access details.
              </p>
              <Button onClick={closeBetaPopup} className="rounded-sm">
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleBetaSubmit} className="space-y-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={betaForm.email}
                  onChange={(e) => setBetaForm({...betaForm, email: e.target.value})}
                  placeholder="you@company.com"
                  className="rounded-sm mt-1"
                  required
                />
              </div>
              
              <div>
                <Label>First Name *</Label>
                <Input
                  value={betaForm.first_name}
                  onChange={(e) => setBetaForm({...betaForm, first_name: e.target.value})}
                  placeholder="John"
                  className="rounded-sm mt-1"
                  required
                />
              </div>
              
              <div>
                <Label>Company/Business Name *</Label>
                <Input
                  value={betaForm.company_name}
                  onChange={(e) => setBetaForm({...betaForm, company_name: e.target.value})}
                  placeholder="Smith Construction"
                  className="rounded-sm mt-1"
                  required
                />
              </div>
              
              <div>
                <Label>Phone Number <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input
                  type="tel"
                  value={betaForm.phone}
                  onChange={(e) => setBetaForm({...betaForm, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                  className="rounded-sm mt-1"
                />
              </div>
              
              <div>
                <Label># of Employees <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Select
                  value={betaForm.num_employees}
                  onValueChange={(val) => setBetaForm({...betaForm, num_employees: val})}
                >
                  <SelectTrigger className="rounded-sm mt-1">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYEE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="mb-2 block">Biggest Pain Points <span className="text-slate-400 font-normal">(optional)</span></Label>
                <div className="space-y-2">
                  {PAIN_POINTS.map(point => (
                    <div key={point.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`popup-${point.id}`}
                        checked={betaForm.pain_points.includes(point.id)}
                        onCheckedChange={() => handleBetaPainPointToggle(point.id)}
                      />
                      <label htmlFor={`popup-${point.id}`} className="text-sm cursor-pointer">
                        {point.label}
                      </label>
                    </div>
                  ))}
                </div>
                {betaForm.pain_points.includes('other') && (
                  <Input
                    value={betaForm.other_pain_point}
                    onChange={(e) => setBetaForm({...betaForm, other_pain_point: e.target.value})}
                    placeholder="Please specify..."
                    className="rounded-sm mt-2"
                  />
                )}
              </div>
              
              <div>
                <Label>ZIP Code <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input
                  value={betaForm.zip_code}
                  onChange={(e) => setBetaForm({...betaForm, zip_code: e.target.value})}
                  placeholder="90210"
                  className="rounded-sm mt-1 w-32"
                />
              </div>
              
              <Button
                type="submit"
                disabled={betaSubmitting}
                className="w-full rounded-sm bg-orange-500 hover:bg-orange-600 h-11"
              >
                {betaSubmitting ? 'Submitting...' : 'Join the Waitlist'}
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                By signing up, you agree to receive emails about Corbel.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
